import axios from 'axios'
import * as common from './common'
import * as api from './api'
import * as constant from './constant'

/**
 * 从 query 字符串解析 sid（含 redirect 参数内嵌的 sid）
 */
export const parseSidFromSearch = (search = '') => {
  if (!search) return ''
  const normalized = search.startsWith('?') ? search.slice(1) : search
  try {
    const params = new URLSearchParams(normalized)
    const sid = params.get('sid')
    if (sid) return String(sid)
    const redirect = params.get('redirect')
    if (redirect) {
      const decoded = decodeURIComponent(redirect)
      const match = decoded.match(/[?&]sid=([^&]+)/)
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }
    }
  } catch (e) {
    // ignore
  }
  return ''
}

/** 从 hash 路由地址解析 sid（应用启动时尚未进入 vue-router） */
export const getSidFromLocationHash = () => {
  const hash = window.location.hash || ''
  const qIndex = hash.indexOf('?')
  if (qIndex === -1) return ''
  return parseSidFromSearch(hash.slice(qIndex))
}

/** 从 vue-router route 解析 sid */
export const resolveSidFromRoute = (route) => {
  if (!route) return ''
  if (route.query && route.query.sid) {
    return String(route.query.sid)
  }
  if (route.query && route.query.redirect) {
    const redirect = String(route.query.redirect)
    try {
      const decoded = redirect.startsWith('/') ? redirect : decodeURIComponent(redirect)
      const match = decoded.match(/[?&]sid=([^&]+)/)
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }
    } catch (e) {
      // ignore
    }
  }
  return ''
}

let lastSyncedParkSid = ''
let syncingParkSidPromise = null

/**
 * 路由进入时：以 URL 中的 sid 为准同步 Vuex 并拉取车场名称更新顶部标题
 */
export const syncParkContextFromRoute = (route, store) => {
  const sid = resolveSidFromRoute(route)
  if (!sid) {
    return Promise.resolve(null)
  }
  if (store) {
    const currentSid = store.state.sid && store.state.sid.sid
    if (currentSid !== sid) {
      store.commit(constant.keySidVuex, sid)
    }
  }
  if (sid === lastSyncedParkSid && syncingParkSidPromise) {
    return syncingParkSidPromise
  }
  lastSyncedParkSid = sid
  syncingParkSidPromise = loadParkInfoAndSyncTitle(sid)
    .catch((err) => {
      console.error('syncParkContextFromRoute failed:', err)
      return null
    })
    .finally(() => {
      if (lastSyncedParkSid === sid) {
        syncingParkSidPromise = null
      }
    })
  return syncingParkSidPromise
}

/**
 * 读取本地缓存的车场名称（兼容旧 key parkName，并迁移到 park_name）
 */
export const getCachedParkName = () => {
  let name = localStorage.getItem(constant.keyParkNameLocalStorage)
  if (!name) {
    name = localStorage.getItem('parkName')
    if (name) {
      localStorage.setItem(constant.keyParkNameLocalStorage, name)
      localStorage.removeItem('parkName')
    }
  }
  return name || null
}

/**
 * 兼容微信 iOS 内核对 document.title 刷新不及时的问题
 */
const refreshWechatIosTitle = (parkname) => {
  const ua = navigator.userAgent.toLowerCase()
  if (!ua.includes('micromessenger') || !ua.includes('iphone')) {
    return
  }
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = '/favicon.ico'
  const removeIframe = () => {
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe)
      }
    }, 0)
  }
  iframe.onload = removeIframe
  iframe.onerror = removeIframe
  document.body.appendChild(iframe)
  document.title = parkname
}

/**
 * 同步微信/浏览器顶部标题与车场名称缓存
 */
export const applyParkBrowserTitle = (parkname) => {
  if (!parkname) return
  document.title = parkname
  localStorage.setItem(constant.keyParkNameLocalStorage, parkname)
  localStorage.removeItem('parkName')
  if (document.body) {
    refreshWechatIosTitle(parkname)
  }
}

/**
 * 页面进入时先用缓存恢复顶部标题，避免短暂显示默认「停车场」
 */
export const restoreParkBrowserTitleFromCache = () => {
  const name = getCachedParkName()
  if (name) {
    applyParkBrowserTitle(name)
  }
  return name
}

/**
 * 根据 sid 查询车场信息，并同步浏览器标题（与首页 query_customer_by_sid 一致）
 */
export const loadParkInfoAndSyncTitle = async (sid, options = {}) => {
  const parkInfo = await queryCustomerBySid(sid, options)
  if (parkInfo && parkInfo.parkname) {
    applyParkBrowserTitle(parkInfo.parkname)
  }
  return parkInfo
}

/**
 * 根据车场ID查询车场信息
 * @param {string|number} sid - 车场ID
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<Object|null>} 返回车场信息对象，如果找不到则返回null
 */
export const queryCustomerBySid = async (sid, options = {}) => {
  const {
    contentType = 'application/json'
  } = options

  try {
    const res = await axios({
      url: common.cloudParkingUrl + '/s2p/' + api.urlList.query_customer,
      method: 'POST',
      data: { sid },
      header: {
        'content-type': contentType
      }
    })

    const d = res && res.data
    if (d.code === 10000) {
      const parkInfo = {
        total_spaces: d.list[0].mix_info?.total_spaces || 100,
        parkname: d.list[0].alias_name || '车场',
        avatar: d.list[0].avatar,
        feeRule: d.list[0].feeRule || 500
      }

      // 保存车场名称到本地存储
      if (parkInfo.parkname) {
        localStorage.setItem(constant.keyParkNameLocalStorage, parkInfo.parkname)
      }

      return parkInfo
    } else {
      console.error('查询车场信息失败:', d.msg || '未知错误')
      return null
    }
  } catch (error) {
    console.error('查询车场信息异常:', error)
    return null
  }
}

/**
 * 获取车场名称（简化版本）
 * @param {string|number} sid - 车场ID
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<string|null>} 返回车场名称，如果找不到则返回null
 */
export const getParkName = async (sid, options = {}) => {
  const parkInfo = await queryCustomerBySid(sid, options)
  return parkInfo ? parkInfo.parkname : null
}

/**
 * 获取车场完整信息
 * @param {string|number} sid - 车场ID
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<Object|null>} 返回车场完整信息，如果找不到则返回null
 */
export const getParkInfo = async (sid, options = {}) => {
  return await queryCustomerBySid(sid, options)
}

/**
 * 获取支付参数
 * @param {Object} obj - 支付对象参数
 * @param {string} obj.sid - 车场ID
 * @param {string} obj.plate_number - 车牌号
 * @param {number} obj.gross_price - 总价
 * @param {number} obj.actually_paid - 实付金额
 * @param {string} obj.subject - 支付主题
 * @param {string} obj.start_time - 开始时间
 * @param {string} obj.end_time - 结束时间
 * @param {Object} obj.local_info - 本地信息
 * @param {string} obj.sign - 签名
 * @param {boolean} obj.is_xcx - 是否小程序
 * @param {string} obj.code - 授权码
 * @param {string} openid - 微信openid
 * @param {string|number} type - 支付类型
 * @param {string|number} payChannelPlatform - 支付渠道平台
 * @param {boolean} isWeixin - 是否为微信环境，默认为true
 * @param {Object} options - 配置选项
 * @param {string} options.contentType - 请求头类型，默认为 'application/json'
 * @returns {Promise<Object>} 返回支付参数
 */
export const getPayargs = async (obj, openid, type, payChannelPlatform, isWeixin = true, options = {}) => {
  const {
    contentType = 'application/json'
  } = options

  try {
    let path
    const pcp = payChannelPlatform || common.payPlatform.pp
    type = type || localStorage.getItem('pay_type')

    if (isWeixin) {
      path = 'pay.wx.do_h5'
      if (!type || type === 'undefined') type = parseInt(pcp) + common.payType.wx
      if (!type) type = common.payType.wx
    } else {
      path = 'pay.zfb.do_h5'
      if (!type || type === 'undefined') type = parseInt(pcp) + common.payType.zfb
      if (!type) type = common.payType.zfb
    }

    console.log('type', type)
    console.log(obj, 'util参数')

    const res = await axios({
      url: common.cloudParkingUrl + '/s2p/' + path,
      method: 'POST',
      data: {
        type: type,
        code: openid == null ? obj.code : openid,
        sid: obj.sid,
        plate_number: obj.plate_number,
        gross_price: obj.gross_price,
        actually_paid: obj.actually_paid,
        subject: obj.subject,
        start_time: obj.start_time,
        end_time: obj.end_time,
        local_info: obj.local_info,
        sign: obj.sign,
        is_xcx: obj.is_xcx
      },
      header: {
        'content-type': contentType
      }
    })

    console.log(res, '获取支付')
    const d = res && res.data
    if (d.code === 10000) {
      return d.payargs
    } else {
      const err = new Error(d.msg || '支付失败')
      err.code = d.code
      throw err
    }
  } catch (error) {
    console.error('获取支付参数异常:', error)
    throw error
  }
}
